/**
 * Single product page — price swap (variant → range), sticky cart bar (mobile).
 *
 * Wymaga jQuery (WC variation script używa custom events `found_variation`
 * / `reset_data` na `.variations_form`, dispatchowane przez jQuery, nie DOM).
 */
export function initSingleProduct() {
    const priceWrap = document.querySelector('.hn-product__price-wrap');
    const form = document.querySelector('.variations_form');

    if (priceWrap && form && window.jQuery) {
        const $ = window.jQuery;
        const defaultPrice = priceWrap.getAttribute('data-default-price') || priceWrap.innerHTML;

        $(form)
            .on('found_variation', (_e, variation) => {
                if (variation && variation.price_html) {
                    priceWrap.innerHTML = variation.price_html;
                }
            })
            .on('reset_data', () => {
                priceWrap.innerHTML = defaultPrice;
            });
    }

    initStickyCart();
    initDeliveryCountdown();
    initSubscriptionInfoToggle();
    initGalleryLightbox();
}

/**
 * Klik w główne zdjęcie galerii otwiera pełnoekranowy PhotoSwipe.
 *
 * WooCommerce (ta wersja) otwiera PhotoSwipe WYŁĄCZNIE przez `.woocommerce-product-gallery__trigger`
 * (lupka), którą motyw chowa `display:none` (patrz _single-product.scss) w założeniu, że klik w samo
 * zdjęcie i tak otwiera — a nie otwiera (WC nie wiąże open na kliknięciu obrazka). Dodatkowo
 * jQuery.zoom nakłada `img.zoomImg` na wierzch slajdu, więc klik nie dochodzi do anchora.
 * Delegujemy na slajd: zoomImg / img / anchor to jego dzieci, więc klik tu bąbelkuje →
 * wywołujemy ukryty trigger, który otwiera lightbox na bieżącym zdjęciu. Strzałki flexslidera
 * (`.flex-direction-nav`) wykluczone, żeby nawigacja slajdów działała normalnie.
 */
function initGalleryLightbox() {
    document.addEventListener('click', (e) => {
        const slide = e.target.closest('.hn-product__gallery .woocommerce-product-gallery__image');
        if (!slide) return;
        if (e.target.closest('.flex-direction-nav')) return;
        const gallery = slide.closest('.woocommerce-product-gallery');
        const trigger = gallery && gallery.querySelector('.woocommerce-product-gallery__trigger');
        if (!trigger) return;
        e.preventDefault();
        trigger.click();
    });
}

/**
 * Toggle [data-hn-sub-info] — pokazuje benefit list (dostawa auto, -10%, anuluj 1-klik)
 * tylko gdy user wybierze opcję subskrypcji w wcsatt prompt radio.
 *
 * Plugin "All Products for Subscriptions" renderuje:
 *   <input name="subscribe-to-action-input" value="yes"> ← subskrypcja
 *   <input name="subscribe-to-action-input" value="no">  ← jednorazowo
 * Default hidden, show gdy value="yes".
 */
function initSubscriptionInfoToggle() {
    const info = document.querySelector('[data-hn-sub-info]');
    if (!info) return;

    // PHP renderuje element z `hidden` attribute (no-JS fallback). Usuwamy raz na
    // init — od teraz visibility kontroluje klasa `.is-active` (animacja max-height
    // + opacity + padding w CSS). Bez tego `display:none!important` z [hidden]
    // ubija animację.
    info.removeAttribute('hidden');

    const sync = () => {
        const checked = document.querySelector('input[name="subscribe-to-action-input"]:checked');
        const isSub = checked?.value === 'yes';
        info.classList.toggle('is-active', isSub);
    };

    document.addEventListener('change', (e) => {
        if (e.target.matches?.('input[name="subscribe-to-action-input"]')) sync();
    });
    sync(); // initial state — plugin może pre-selectować opcję
}

/**
 * Countdown do 13:00 w dni robocze (Pn–Pt). Zamówienia po 13:00 lub w weekend
 * przesuwają deadline na 13:00 najbliższego dnia roboczego.
 *
 * Data dostawy = deadline_day + 1 dzień roboczy (uproszczony model — InPost/kurier
 * następnego dnia roboczego). Można podmienić logikę gdy klient ustali rzeczywisty SLA.
 */
function initDeliveryCountdown() {
    const root = document.querySelector('[data-hn-delivery]');
    if (!root) return;

    const dateEl = root.querySelector('[data-hn-delivery-date]');
    const cdEl = root.querySelector('[data-hn-delivery-countdown]');
    if (!dateEl || !cdEl) return;

    const DAY_NAMES = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];
    const MONTH_NAMES = [
        'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
        'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'
    ];

    // Dni bez wysyłki i godzina cut-offu przychodzą z PHP (inc/shipping-holidays.php)
    // przez wp_localize_script. Wcześniej ten skrypt liczył dni robocze po swojemu i znał
    // tylko weekendy — przez co nadpisywał poprawną datę z PHP błędną w każde święto.
    const SHIPPING = window.HN_SHIPPING || {};
    const HOLIDAYS = new Set(Array.isArray(SHIPPING.holidays) ? SHIPPING.holidays : []);
    const CUTOFF_HOUR = Number.isInteger(SHIPPING.cutoffHour) ? SHIPPING.cutoffHour : 13;

    const ymd = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const isShippingDay = (d) => d.getDay() >= 1 && d.getDay() <= 5 && !HOLIDAYS.has(ymd(d));

    const nextWorkday = (d) => {
        const next = new Date(d);
        let guard = 0;
        do {
            next.setDate(next.getDate() + 1);
        } while (!isShippingDay(next) && guard++ < 30);
        return next;
    };

    const computeDeadline = (now) => {
        const cutoff = new Date(now);
        cutoff.setHours(CUTOFF_HOUR, 0, 0, 0);
        if (now < cutoff && isShippingDay(now)) return cutoff;
        const next = nextWorkday(now);
        next.setHours(CUTOFF_HOUR, 0, 0, 0);
        return next;
    };

    const formatDeliveryDay = (deadline) => {
        const d = nextWorkday(deadline);
        return `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
    };

    const formatCountdown = (ms) => {
        const totalSec = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(h)}h ${pad(m)}m`;
    };

    let deadline = computeDeadline(new Date());
    dateEl.textContent = formatDeliveryDay(deadline);

    const tick = () => {
        const now = new Date();
        if (now >= deadline) {
            deadline = computeDeadline(now);
            dateEl.textContent = formatDeliveryDay(deadline);
        }
        cdEl.textContent = formatCountdown(deadline - now);
    };

    tick();
    setInterval(tick, 1000);
}

function initStickyCart() {
    const sticky = document.querySelector('[data-hn-sticky-cart]');
    const cartForm = document.querySelector('.hn-product__cart-wrap form.cart');
    if (!sticky || !cartForm) return;

    // Pokazuj sticky tylko gdy główny form scrollnął poza viewport (na mobile).
    const observer = new IntersectionObserver(
        ([entry]) => {
            const isVisible = entry.isIntersecting;
            sticky.setAttribute('data-show', isVisible ? 'false' : 'true');
            sticky.setAttribute('aria-hidden', isVisible ? 'true' : 'false');
        },
        { threshold: 0, rootMargin: '0px 0px -120px 0px' }
    );
    observer.observe(cartForm);

    // Klik "Dodaj do koszyka" w sticky bar → submit prawdziwego forma.
    const stickyBtn = sticky.querySelector('[data-hn-sticky-add]');
    if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
            const submit = cartForm.querySelector('button[type="submit"], .single_add_to_cart_button');
            if (submit) {
                submit.click();
            } else {
                cartForm.requestSubmit ? cartForm.requestSubmit() : cartForm.submit();
            }
        });
    }

    // Wiggle CTA — gdy sticky widoczny przez >=5s bez kliku, lekka animacja
    // "weź mnie". Tylko raz na sesję żeby nie irytować.
    if (stickyBtn && !sessionStorage.getItem('hn_sticky_wiggled')) {
        let wiggleTimer = null;
        const startWiggleTimer = () => {
            if (sessionStorage.getItem('hn_sticky_wiggled')) return;
            clearTimeout(wiggleTimer);
            wiggleTimer = setTimeout(() => {
                if (sticky.getAttribute('data-show') === 'true') {
                    stickyBtn.classList.add('is-wiggle');
                    sessionStorage.setItem('hn_sticky_wiggled', '1');
                    setTimeout(() => stickyBtn.classList.remove('is-wiggle'), 1000);
                }
            }, 5000);
        };

        // Restart timer przy każdej zmianie widoczności sticky (user może wracać).
        const visObserver = new MutationObserver(() => {
            if (sticky.getAttribute('data-show') === 'true') startWiggleTimer();
        });
        visObserver.observe(sticky, { attributes: true, attributeFilter: ['data-show'] });

        stickyBtn.addEventListener('click', () => {
            sessionStorage.setItem('hn_sticky_wiggled', '1');
            clearTimeout(wiggleTimer);
        });
    }
}

/**
 * Heart-bump dla wishlist klików.
 * Listenuje na każdy klik na [data-hn-heart-bump] i dodaje klasę is-bumped na 500ms.
 */
function initHeartBump() {
    document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-hn-heart-bump], .hn-heart-bump');
        if (!target) return;
        target.classList.add('is-bumped');
        setTimeout(() => target.classList.remove('is-bumped'), 500);
    });
}

// Init heart-bump globalnie (działa na każdej stronie z wishlist heart'ami).
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initHeartBump);
    } else {
        initHeartBump();
    }
}
